import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Header from '../components/Header';
import ImageUploader from '../components/ImageUploader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { NotificationSettings } from '../components/NotificationSoundSettings';
import { useOnboarding } from '../components/OnboardingWalkthrough';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, Camera, CreditCard, Shield, Bell, Lock, 
  ChevronLeft, Save, Loader2, Phone, Mail, MapPin,
  Key, Smartphone, Building2, Eye, EyeOff, Home,
  Sun, Moon, HelpCircle, RotateCcw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const { user, getAuthHeaders, refreshUser, logout } = useAuth();
  const { theme, setTheme, isDark } = useTheme();
  const { startOnboarding, resetOnboarding } = useOnboarding();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profilePicture, setProfilePicture] = useState(user?.picture || null);
  const [profileImages, setProfileImages] = useState([]);
  
  // Profile settings
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    location: ''
  });
  
  // Notification settings
  const [notifications, setNotifications] = useState({
    email_notifications: true,
    sms_notifications: true,
    push_notifications: true,
    marketing_emails: false
  });
  
  // Security settings
  const [security, setSecurity] = useState({
    two_factor_enabled: false,
    two_factor_method: 'app'  // 'app' or 'sms'
  });
  
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorSecret, setTwoFactorSecret] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  
  // Password change
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (profileImages.length > 0 && profileImages[0] !== profilePicture) {
      handleProfilePictureChange(profileImages[0]);
    }
  }, [profileImages]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(
        `${API}/user/settings`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      setProfile(prev => ({
        ...prev,
        ...response.data.profile
      }));
      setNotifications(response.data.notifications || notifications);
      setSecurity(response.data.security || security);
      setProfilePicture(response.data.profile?.picture || user?.picture);
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureChange = async (pictureUrl) => {
    try {
      await axios.put(
        `${API}/profile/picture`,
        { picture_url: pictureUrl },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setProfilePicture(pictureUrl);
      toast.success('Profile picture updated!');
      refreshUser?.();
    } catch (error) {
      toast.error('Failed to update profile picture');
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/user/settings/profile`,
        profile,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Profile updated!');
      refreshUser?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/user/settings/notifications`,
        notifications,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Notification preferences updated!');
    } catch (error) {
      toast.error('Failed to update notifications');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.new.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/user/settings/password`,
        {
          current_password: passwords.current,
          new_password: passwords.new
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      toast.success('Password changed successfully!');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const enable2FA = async () => {
    try {
      const response = await axios.post(
        `${API}/user/settings/2fa/setup`,
        { method: security.two_factor_method },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setTwoFactorSecret(response.data);
      setShowTwoFactorSetup(true);
    } catch (error) {
      toast.error('Failed to setup 2FA');
    }
  };

  const verify2FA = async () => {
    if (twoFactorCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifyingCode(true);
    try {
      await axios.post(
        `${API}/user/settings/2fa/verify`,
        { code: twoFactorCode },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setSecurity(prev => ({ ...prev, two_factor_enabled: true }));
      setShowTwoFactorSetup(false);
      setTwoFactorCode('');
      toast.success('Two-factor authentication enabled!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setVerifyingCode(false);
    }
  };

  const disable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication?')) return;
    
    try {
      await axios.post(
        `${API}/user/settings/2fa/disable`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setSecurity(prev => ({ ...prev, two_factor_enabled: false }));
      toast.success('Two-factor authentication disabled');
    } catch (error) {
      toast.error('Failed to disable 2FA');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="settings-page">
      <Header />
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Navigation */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Link to="/">
            <Button variant="ghost" size="icon" data-testid="home-btn">
              <Home className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-white dark:bg-gray-800 border dark:border-gray-700 w-full justify-start flex-wrap">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" /> Profile
            </TabsTrigger>
            <TabsTrigger value="appearance" className="flex items-center gap-2">
              {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />} Appearance
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="w-4 h-4" /> Notifications
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Lock className="w-4 h-4" /> Security
            </TabsTrigger>
            <TabsTrigger value="payment" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Payment
            </TabsTrigger>
          </TabsList>

          {/* Appearance Tab */}
          <TabsContent value="appearance">
            <div className="space-y-6">
              {/* Theme Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                    Theme
                  </CardTitle>
                  <CardDescription>Choose your preferred color scheme</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setTheme('light')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        theme === 'light'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-12 h-12 mx-auto mb-3 bg-white border rounded-full flex items-center justify-center">
                        <Sun className="w-6 h-6 text-yellow-500" />
                      </div>
                      <p className="font-medium text-center">Light</p>
                      <p className="text-xs text-gray-500 text-center mt-1">Default</p>
                    </button>
                    <button
                      onClick={() => setTheme('dark')}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        theme === 'dark'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="w-12 h-12 mx-auto mb-3 bg-gray-900 border border-gray-700 rounded-full flex items-center justify-center">
                        <Moon className="w-6 h-6 text-blue-400" />
                      </div>
                      <p className="font-medium text-center">Dark</p>
                      <p className="text-xs text-gray-500 text-center mt-1">Easy on eyes</p>
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Onboarding / Help */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HelpCircle className="w-5 h-5" />
                    Help & Walkthrough
                  </CardTitle>
                  <CardDescription>Learn how to use the app features</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Feature Walkthrough</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Interactive tour of all app features
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        resetOnboarding();
                        startOnboarding();
                        toast.success('Walkthrough started!');
                      }}
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Start Tour
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="space-y-6">
              {/* Profile Picture */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Camera className="w-5 h-5" />
                    Profile Picture
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full bg-gray-100 border-4 border-gray-200 overflow-hidden flex items-center justify-center">
                        {profilePicture ? (
                          <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <User className="w-12 h-12 text-gray-300" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1">
                      <ImageUploader
                        images={profileImages}
                        setImages={setProfileImages}
                        maxImages={1}
                        getAuthHeaders={getAuthHeaders}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Basic Info */}
              <Card>
                <CardHeader>
                  <CardTitle>Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={profile.name}
                      onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        value={profile.email}
                        onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <Input
                        id="phone"
                        value={profile.phone}
                        onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="(555) 123-4567"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="location">Location</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <Input
                        id="location"
                        value={profile.location}
                        onChange={(e) => setProfile(prev => ({ ...prev, location: e.target.value }))}
                        placeholder="Fort Wayne, IN"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <Button onClick={saveProfile} disabled={saving} className="w-full">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>Choose how you want to receive updates</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Email Notifications</p>
                    <p className="text-sm text-gray-500">Receive service updates via email</p>
                  </div>
                  <Switch
                    checked={notifications.email_notifications}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, email_notifications: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">SMS Notifications</p>
                    <p className="text-sm text-gray-500">Receive text messages for urgent updates</p>
                  </div>
                  <Switch
                    checked={notifications.sms_notifications}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, sms_notifications: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Push Notifications</p>
                    <p className="text-sm text-gray-500">Browser push notifications</p>
                  </div>
                  <Switch
                    checked={notifications.push_notifications}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, push_notifications: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Marketing Emails</p>
                    <p className="text-sm text-gray-500">Promotions and special offers</p>
                  </div>
                  <Switch
                    checked={notifications.marketing_emails}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, marketing_emails: checked }))}
                  />
                </div>
                <Button onClick={saveNotifications} disabled={saving} className="w-full">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Preferences
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <div className="space-y-6">
              {/* Password Change */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    Change Password
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="current-password">Current Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="current-password"
                        type={showPasswords ? 'text' : 'password'}
                        value={passwords.current}
                        onChange={(e) => setPasswords(prev => ({ ...prev, current: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type={showPasswords ? 'text' : 'password'}
                      value={passwords.new}
                      onChange={(e) => setPasswords(prev => ({ ...prev, new: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirm-password">Confirm New Password</Label>
                    <Input
                      id="confirm-password"
                      type={showPasswords ? 'text' : 'password'}
                      value={passwords.confirm}
                      onChange={(e) => setPasswords(prev => ({ ...prev, confirm: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={showPasswords}
                      onCheckedChange={setShowPasswords}
                    />
                    <span className="text-sm text-gray-600">Show passwords</span>
                  </div>
                  <Button onClick={changePassword} disabled={saving || !passwords.current || !passwords.new}>
                    Change Password
                  </Button>
                </CardContent>
              </Card>

              {/* Two-Factor Authentication */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5" />
                    Two-Factor Authentication
                  </CardTitle>
                  <CardDescription>
                    Add an extra layer of security to your account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {security.two_factor_enabled ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <Shield className="w-6 h-6 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">2FA is Enabled</p>
                          <p className="text-sm text-green-600">Your account has extra security</p>
                        </div>
                      </div>
                      <Button variant="destructive" onClick={disable2FA}>
                        Disable 2FA
                      </Button>
                    </div>
                  ) : showTwoFactorSetup ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="font-medium mb-2">Setup Instructions:</p>
                        <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                          <li>Download an authenticator app (Google Authenticator, Authy)</li>
                          <li>Scan the QR code or enter the secret key manually</li>
                          <li>Enter the 6-digit code from the app below</li>
                        </ol>
                      </div>
                      {twoFactorSecret?.qr_code && (
                        <div className="flex justify-center p-4 bg-white rounded-lg border">
                          <img src={twoFactorSecret.qr_code} alt="2FA QR Code" className="w-48 h-48" />
                        </div>
                      )}
                      {twoFactorSecret?.secret && (
                        <div className="p-3 bg-gray-100 rounded font-mono text-sm text-center">
                          {twoFactorSecret.secret}
                        </div>
                      )}
                      <div>
                        <Label>Verification Code</Label>
                        <Input
                          value={twoFactorCode}
                          onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="Enter 6-digit code"
                          maxLength={6}
                          className="mt-1 text-center text-2xl tracking-widest"
                        />
                      </div>
                      <div className="flex gap-3">
                        <Button variant="outline" onClick={() => setShowTwoFactorSetup(false)} className="flex-1">
                          Cancel
                        </Button>
                        <Button onClick={verify2FA} disabled={verifyingCode || twoFactorCode.length !== 6} className="flex-1">
                          {verifyingCode ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                          Verify & Enable
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label>Authentication Method</Label>
                        <Select 
                          value={security.two_factor_method} 
                          onValueChange={(value) => setSecurity(prev => ({ ...prev, two_factor_method: value }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="app">Authenticator App (Recommended)</SelectItem>
                            <SelectItem value="sms">SMS Text Message</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={enable2FA}>
                        <Shield className="w-4 h-4 mr-2" />
                        Enable 2FA
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Payment Tab */}
          <TabsContent value="payment">
            <div className="space-y-6">
              {/* Plaid Bank Connection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Connect Bank Account
                  </CardTitle>
                  <CardDescription>
                    Securely link your bank account for faster payments and get 10% off all services!
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <PlaidLinkSection getAuthHeaders={getAuthHeaders} onSuccess={fetchSettings} />
                </CardContent>
              </Card>

              {/* Manage All Payment Methods */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    All Payment Methods
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Link to="/profile/setup">
                    <Button className="w-full">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Manage Cards & Bank Accounts
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// Plaid Link Section Component
function PlaidLinkSection({ getAuthHeaders, onSuccess }) {
  const [linkToken, setLinkToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPlaidUI, setShowPlaidUI] = useState(false);
  const [bankData, setBankData] = useState({
    institutionName: '',
    accountName: 'Checking Account',
    accountMask: '',
    enableAutopay: true
  });
  
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const initPlaidLink = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/plaid/create_link_token`,
        {},
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setLinkToken(response.data.link_token);
      setShowPlaidUI(true);
      toast.info('Plaid Link initialized (MOCKED MODE)');
    } catch (error) {
      toast.error('Failed to initialize bank connection');
    } finally {
      setLoading(false);
    }
  };

  const handlePlaidSuccess = async () => {
    if (!bankData.institutionName || !bankData.accountMask) {
      toast.error('Please fill in bank details');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/plaid/exchange_public_token`,
        {
          public_token: `public-sandbox-${linkToken}`,
          institution_name: bankData.institutionName,
          account_name: bankData.accountName,
          account_mask: bankData.accountMask,
          enable_autopay: bankData.enableAutopay
        },
        { headers: getAuthHeaders(), withCredentials: true }
      );
      
      toast.success(response.data.message || 'Bank account connected!');
      setShowPlaidUI(false);
      setBankData({ institutionName: '', accountName: 'Checking Account', accountMask: '', enableAutopay: true });
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect bank account');
    } finally {
      setLoading(false);
    }
  };

  if (showPlaidUI) {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800 font-medium mb-2">
            🔐 Plaid Secure Bank Connection (Demo Mode)
          </p>
          <p className="text-xs text-blue-600">
            In production, this would open Plaid's secure bank login. For demo, enter your bank details below.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="institutionName">Bank Name</Label>
            <Input
              id="institutionName"
              value={bankData.institutionName}
              onChange={(e) => setBankData(prev => ({ ...prev, institutionName: e.target.value }))}
              placeholder="Chase, Bank of America, etc."
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="accountName">Account Type</Label>
            <Input
              id="accountName"
              value={bankData.accountName}
              onChange={(e) => setBankData(prev => ({ ...prev, accountName: e.target.value }))}
              placeholder="Checking Account"
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="accountMask">Last 4 Digits of Account</Label>
            <Input
              id="accountMask"
              value={bankData.accountMask}
              onChange={(e) => setBankData(prev => ({ ...prev, accountMask: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
              placeholder="1234"
              maxLength={4}
              className="mt-1"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={bankData.enableAutopay}
              onCheckedChange={(checked) => setBankData(prev => ({ ...prev, enableAutopay: checked }))}
            />
            <span className="text-sm">Enable Auto-Pay (Get 10% off all services!)</span>
          </div>
        </div>

        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => setShowPlaidUI(false)}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button 
            onClick={handlePlaidSuccess}
            disabled={loading || !bankData.institutionName || !bankData.accountMask}
            className="flex-1"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
            Connect Account
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
        <Shield className="w-6 h-6 text-green-600" />
        <div>
          <p className="font-medium text-green-800">Secure Bank Verification</p>
          <p className="text-sm text-green-600">Connect via Plaid for instant verification & 10% service discount</p>
        </div>
      </div>
      
      <Button 
        onClick={initPlaidLink}
        disabled={loading}
        className="w-full bg-green-600 hover:bg-green-700"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Building2 className="w-4 h-4 mr-2" />}
        Connect Bank with Plaid
      </Button>
    </div>
  );
}
